/**
 * useImageModels — fetches entitlement-filtered image models from the backend.
 */

import { useCallback, useEffect, useState } from 'react'

import {
  getImageModels,
  type ByomModelInfo,
  type ImageModelsResponse,
} from '../api/client'
import { createNadDebug } from '../utils/debugLog'

const log = createNadDebug('image-models')

interface UseImageModelsResult {
  native: string[]
  byom: ByomModelInfo[]
  defaultModel: string | null
  isLoading: boolean
  error: string | null
  refetch: () => void
}

const EMPTY_RESPONSE: ImageModelsResponse = {
  native: [],
  byom: [],
  default_model: null,
}
let cachedResponse: ImageModelsResponse = EMPTY_RESPONSE
let gatewayConfigVersion = 0
const listeners = new Set<() => void>()

export function notifyImageModelConfigChanged(): void {
  gatewayConfigVersion++
  for (const listener of listeners) {
    listener()
  }
}

/** @internal — reset module-level cache for tests. */
export function _resetImageModelCacheForTests(): void {
  cachedResponse = EMPTY_RESPONSE
  gatewayConfigVersion = 0
  listeners.clear()
}

export function useImageModels(): UseImageModelsResult {
  const [data, setData] = useState<ImageModelsResponse>(cachedResponse)
  const [isLoading, setIsLoading] = useState(cachedResponse === EMPTY_RESPONSE)
  const [error, setError] = useState<string | null>(null)
  const [version, setVersion] = useState(gatewayConfigVersion)

  const refetch = useCallback(() => {
    setVersion((v) => v + 1)
  }, [])

  useEffect(() => {
    const handler = () => setVersion(gatewayConfigVersion)
    listeners.add(handler)
    return () => {
      listeners.delete(handler)
    }
  }, [])

  useEffect(() => {
    let cancelled = false
    if (cachedResponse === EMPTY_RESPONSE) {
      setIsLoading(true)
    }
    setError(null)

    const cacheWarm = cachedResponse !== EMPTY_RESPONSE
    log({ event: 'fetch:start', trace_id: 'image-models:fetch', cache_warm: cacheWarm })

    getImageModels()
      .then((result) => {
        if (!cancelled) {
          log({
            event: 'fetch:ok',
            trace_id: 'image-models:fetch',
            native_count: result.native.length,
            byom_count: result.byom?.length ?? 0,
            default_model: result.default_model,
          })
          cachedResponse = result
          setData(result)
          setIsLoading(false)
        }
      })
      .catch((err) => {
        if (!cancelled) {
          log({
            event: 'fetch:error',
            trace_id: 'image-models:fetch',
            error: err instanceof Error ? err.message : String(err),
          })
          setError(err instanceof Error ? err.message : String(err))
          setIsLoading(false)
        }
      })

    return () => {
      cancelled = true
    }
  }, [version])

  return {
    native: data.native,
    byom: data.byom ?? [],
    defaultModel: data.default_model ?? null,
    isLoading,
    error,
    refetch,
  }
}
